<script setup lang="ts">
import { ref, computed } from 'vue'
import { toRaw } from 'vue'

const props = defineProps({
  data: {
    type: Object,
    required: true
  }
})

const transaction = props.data?.Transaction ?? null
const auth = transaction?.Authorisation ?? transaction?.TransactionHeader ? props.data.Transaction.Authorisation : props.data.Transaction?.Authorisation ?? transaction?.Authorisation ?? props.data?.Transaction?.Authorisation ?? props.data?.Authorisation

// Use sensible fallbacks
const groups = computed(() => (auth?.GroupDetails ?? []).map(g => ({
  ...g,
  AllowedQuantity: Number(g.AllowedQuantity ?? 0),
  UsedQuantity: Number(g.UsedQuantity ?? 0)
})))

const search = ref('')
const statusFilter = ref('')
const expanded = ref(new Set())

function remaining(group) {
  const a = Number(group.AllowedQuantity ?? 0)
  const u = Number(group.UsedQuantity ?? 0)
  const rem = a - u
  return isNaN(rem) ? '–' : rem
}

function remainingClass(group) {
  const rem = Number(group.AllowedQuantity ?? 0) - Number(group.UsedQuantity ?? 0)
  if (rem <= 0) return 'text-red-600'
  if (rem <= Math.max(1, Math.floor((group.AllowedQuantity ?? 0) * 0.15))) return 'text-yellow-600'
  return 'text-green-600'
}

function statusBadgeClass(status) {
  if (!status) return 'bg-gray-100 text-gray-700'
  if (status.toUpperCase() === 'ACTIVE') return 'bg-green-100 text-green-800'
  if (status.toUpperCase() === 'INACTIVE') return 'bg-red-100 text-red-800'
  return 'bg-gray-100 text-gray-700'
}

function toggleExpand(i) {
  if (expanded.value.has(i)) expanded.value.delete(i)
  else expanded.value.add(i)
  // Vue won't track Set mutations for template reactivity, recreate it
  expanded.value = new Set([...expanded.value])
}

function isExpanded(i) {
  return expanded.value.has(i)
}

const filteredGroups = computed(() => {
  const q = search.value.trim().toLowerCase()
  return groups.value.filter(g => {
    if (statusFilter.value) {
      if ((g.LineStatus || '').toUpperCase() !== statusFilter.value.toUpperCase()) return false
    }
    if (!q) return true

    // fields to search: description, charge codes, charge descriptions, specialities
    const desc = (g.GroupTypeDescription || '').toLowerCase()
    if (desc.includes(q)) return true

    const charges = (g.ChargeDetails ?? []).some(c => {
      return (c.ChargeableCode || '').toLowerCase().includes(q) ||
        (c.Description || '').toLowerCase().includes(q) ||
        (c.Publication || '').toLowerCase().includes(q)
    })
    if (charges) return true

    const specs = (g.SpecialityDetails ?? []).some(s => (s.Speciality || '').toLowerCase().includes(q))
    if (specs) return true

    const nums = `${g.AllowedQuantity ?? ''} ${g.UsedQuantity ?? ''}`.toLowerCase()
    if (nums.includes(q)) return true

    return false
  })
})

function prettyPrint(obj) {
  try {
    return JSON.stringify(toRaw(obj), null, 2)
  } catch (e) {
    return String(obj)
  }
}
</script>


<template>
  <div class="space-y-6">
    <!-- Transaction Header -->
    <div class="bg-white shadow rounded-lg p-5">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-lg font-semibold text-gray-700">Transaction</h2>
          <p class="text-sm text-gray-500">Header details for this API response</p>
        </div>
        <div class="text-right">
          <div class="text-sm text-gray-500">Destination</div>
          <div class="text-xl font-medium text-gray-800">{{ transaction?.TransactionHeader?.DestinationIdentifier || '-' }}</div>
        </div>
      </div>

      <div class="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div class="p-3 bg-gray-50 rounded">
          <div class="text-xs text-gray-500">Tran #</div>
          <div class="font-medium text-gray-800">{{ transaction?.TransactionHeader?.TranNum }}</div>
          <div class="text-xs text-gray-500 mt-1">Date</div>
          <div class="text-sm text-gray-600">{{ transaction?.TransactionHeader?.TranDate }}</div>
        </div>

        <div class="p-3 bg-gray-50 rounded">
          <div class="text-xs text-gray-500">Scheme / Option</div>
          <div class="font-medium text-gray-800">{{ auth?.Scheme }} · {{ auth?.OptionCode }}</div>
          <div class="text-xs text-gray-500 mt-1">Auth Seq</div>
          <div class="text-sm text-gray-600">{{ auth?.AuthSeq }}</div>
        </div>

        <div class="p-3 bg-gray-50 rounded">
          <div class="text-xs text-gray-500">Member</div>
          <div class="font-medium text-gray-800">{{ auth?.Firstnames }} {{ auth?.Surname }}</div>
          <div class="text-xs text-gray-500 mt-1">Member No / Dependant</div>
          <div class="text-sm text-gray-600">{{ auth?.MembershipNumber }} · {{ auth?.DependantCode }}</div>
        </div>
      </div>
    </div>

    <!-- Authorisation Summary -->
    <div class="bg-white shadow rounded-lg p-5">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-md font-semibold text-gray-700">Authorisation</h3>
          <p class="text-sm text-gray-500">Program, dates and high-level details</p>
        </div>
        <div class="text-right">
          <div class="text-xs text-gray-500">Program</div>
          <div class="font-medium text-gray-800">{{ auth?.Program || '-' }}</div>
        </div>
      </div>

      <div class="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div class="p-3 border rounded">
          <div class="text-xs text-gray-500">Birthdate</div>
          <div class="font-medium text-gray-800">{{ auth?.BirthDate }}</div>
        </div>
        <div class="p-3 border rounded">
          <div class="text-xs text-gray-500">Authorisation Start</div>
          <div class="font-medium text-gray-800">{{ auth?.AuthorisationStartDate || '-' }}</div>
        </div>
        <div class="p-3 border rounded">
          <div class="text-xs text-gray-500">Tranche</div>
          <div class="font-medium text-gray-800">{{ auth?.Tranche || '-' }}</div>
        </div>
        <div class="p-3 border rounded">
          <div class="text-xs text-gray-500">Authorisation Disease</div>
          <div class="font-medium text-gray-800">
            <template v-if="auth?.AuthorisationDisease?.length">
              {{ auth.AuthorisationDisease[0].ChronicDisease }}
            </template>
            <template v-else>-</template>
          </div>
        </div>
      </div>
    </div>

    <!-- Controls -->
    <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
      <div class="flex items-center gap-2 w-full sm:w-1/2">
        <input
          type="text"
          v-model="search"
          placeholder="Search groups, charges, specialities..."
          class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-300"
        />
      </div>

      <div class="flex items-center gap-3">
        <select v-model="statusFilter" class="px-10 py-2 border rounded-md">
          <option value="">All statuses</option>
          <option value="ACTIVE">ACTIVE</option>
          <option value="INACTIVE">INACTIVE</option>
        </select>

        <div class="text-sm text-gray-600">
          Showing <span class="font-medium text-gray-800">{{ filteredGroups.length }}</span> groups
        </div>
      </div>
    </div>

    <!-- Groups Table -->
    <div class="bg-white shadow rounded-lg overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group</th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Allowed</th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Used</th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining</th>
            <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Extras</th>
            <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-100">
          <tr v-for="(group, idx) in filteredGroups" :key="group.GroupTypeSeq" class="hover:bg-gray-50">
            <td class="px-4 py-3 align-top">
              <div class="text-sm font-medium text-gray-800">{{ group.GroupTypeDescription }}</div>
              <div class="text-xs text-gray-500 mt-1">ChronicSeq: {{ group.ChronicDiseaseSeq }}</div>
              <div class="text-xs text-gray-400 mt-1">Charge codes: 
                <span v-if="group.ChargeDetails?.length">
                  <span v-for="(c, i) in group.ChargeDetails" :key="i" class="inline-flex items-center px-2 py-0.5 mr-1 rounded text-xs bg-indigo-50 text-indigo-700">
                    {{ c.ChargeableCode }}
                  </span>
                </span>
                <span v-else class="text-gray-400">–</span>
              </div>
            </td>

            <td class="px-4 py-3 align-top text-right">
              <div class="text-sm text-gray-800">{{ group.AllowedQuantity ?? '-' }}</div>
            </td>

            <td class="px-4 py-3 align-top text-right">
              <div class="text-sm text-gray-800">{{ group.UsedQuantity ?? 0 }}</div>
            </td>

            <td class="px-4 py-3 align-top text-right">
              <div class="text-sm font-medium" :class="remainingClass(group)">
                {{ remaining(group) }}
              </div>
            </td>

            <td class="px-4 py-3 align-top text-center">
              <span :class="['inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium', statusBadgeClass(group.LineStatus)]">
                {{ group.LineStatus }}
              </span>
            </td>

            <td class="px-4 py-3 align-top">
              <div class="text-sm text-gray-800">{{ group.GroupStartDate || '-' }}</div>
              <div class="text-xs text-gray-500 mt-1">{{ group.GroupEndDate || '—' }}</div>
            </td>

            <td class="px-4 py-3 align-top">
              <div class="text-sm text-gray-700">PMB: <span class="font-medium">{{ group.PMBIndicator || '-' }}</span></div>
              <div class="text-xs text-gray-500 mt-1">Specialities: <span class="font-medium">{{ (group.SpecialityDetails?.length ?? 0) }}</span></div>
            </td>

            <td class="px-4 py-3 align-top text-center">
              <button @click="toggleExpand(idx)" class="inline-flex text-sky-700 border border-sky-700 items-center px-3 py-1 bg-transparent hover:text-white rounded hover:bg-sky-700 text-sm">
                <svg v-if="!isExpanded(idx)" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clip-rule="evenodd"/></svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 transform rotate-90" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clip-rule="evenodd"/></svg>
                {{ isExpanded(idx) ? 'Collapse' : 'Details' }}
              </button>
            </td>
          </tr>

          <!-- Expanded details row -->
          <tr v-for="(group, idx) in filteredGroups" :key="group.GroupTypeSeq + '-details'" v-show="isExpanded(idx)">
            <td :colspan="8" class="px-6 py-4 bg-gray-50">
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 class="text-sm font-semibold text-gray-700 mb-2">Specialities ({{ group.SpecialityDetails?.length ?? 0 }})</h4>
                  <div class="flex flex-wrap gap-2">
                    <template v-if="group.SpecialityDetails?.length">
                      <span v-for="(s, i) in group.SpecialityDetails" :key="i" class="inline-flex items-center px-2 py-1 bg-white border rounded text-xs text-gray-700">
                        {{ s.Speciality }}
                      </span>
                    </template>
                    <div v-else class="text-sm text-gray-500">No specialities</div>
                  </div>
                </div>

                <div>
                  <h4 class="text-sm font-semibold text-gray-700 mb-2">Charge Details ({{ group.ChargeDetails?.length ?? 0 }})</h4>
                  <div class="space-y-2">
                    <template v-if="group.ChargeDetails?.length">
                      <div v-for="(c, i) in group.ChargeDetails" :key="i" class="p-3 bg-white border rounded">
                        <div class="flex items-center justify-between">
                          <div class="text-sm font-medium text-gray-800">{{ c.ChargeableCode || '—' }}</div>
                          <div class="text-xs text-gray-500">{{ c.Publication || '—' }}</div>
                        </div>
                        <div class="text-sm text-gray-600 mt-1">{{ c.Description || '—' }}</div>
                      </div>
                    </template>
                    <div v-else class="text-sm text-gray-500">No charges listed</div>
                  </div>
                </div>
              </div>

              <!-- Raw JSON preview toggle -->
              <div class="mt-4">
                <details class="text-sm text-gray-600">
                  <summary class="cursor-pointer text-indigo-600">Show raw group JSON</summary>
                  <pre class="mt-2 p-3 bg-gray-800 text-gray-100 rounded text-xs overflow-auto">{{ prettyPrint(group) }}</pre>
                </details>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="filteredGroups.length === 0" class="p-6 text-center text-sm text-gray-500">
        No groups match your filters.
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Small tweaks to table scroll on narrow screens */
table { width: 100%; border-collapse: collapse; }
pre { max-height: 320px; }
</style>